import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { Card, CardContent, Field, Input, Textarea } from '~/components/ui/primitives';
import { LoadingState, PageHeader } from '~/components/shared/states';
import { api } from '~/lib/api';
import { queryKeys, messageOf } from '~/lib/queries';

export function AdminCompanyPage() {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.company,
    queryFn: () => api.get<Record<string, string>>('/company'),
  });

  const [form, setForm] = React.useState<Record<string, string>>({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (isLoading)
    return (
      <div className="app-page">
        <LoadingState />
      </div>
    );

  const set = (key: string) => (value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="app-page">
      <PageHeader
        title="Informations de la societe"
        description="Ces informations sont affichees sur le site public et dans les mentions legales."
      />

      <Card className="max-w-3xl">
        <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
          <Field label="Raison sociale" htmlFor="legalName" required>
            <Input
              value={form.legalName ?? ''}
              onChange={(e) => set('legalName')(e.target.value)}
            />
          </Field>
          <Field label="Nom commercial" htmlFor="tradeName" required>
            <Input
              value={form.tradeName ?? ''}
              onChange={(e) => set('tradeName')(e.target.value)}
            />
          </Field>
          <Field label="SIRET" htmlFor="siret">
            <Input
              value={form.siret ?? ''}
              onChange={(e) => set('siret')(e.target.value)}
              maxLength={14}
            />
          </Field>
          <Field label="TVA intracommunautaire" htmlFor="vatNumber">
            <Input
              value={form.vatNumber ?? ''}
              onChange={(e) => set('vatNumber')(e.target.value)}
            />
          </Field>
          <Field label="Adresse e-mail" htmlFor="email" required>
            <Input
              type="email"
              value={form.email ?? ''}
              onChange={(e) => set('email')(e.target.value)}
            />
          </Field>
          <Field label="Telephone" htmlFor="phone" required>
            <Input
              type="tel"
              value={form.phone ?? ''}
              onChange={(e) => set('phone')(e.target.value)}
            />
          </Field>
          <Field label="Rue" htmlFor="street" className="sm:col-span-2">
            <Input value={form.street ?? ''} onChange={(e) => set('street')(e.target.value)} />
          </Field>
          <Field label="Code postal" htmlFor="postalCode">
            <Input
              value={form.postalCode ?? ''}
              onChange={(e) => set('postalCode')(e.target.value)}
            />
          </Field>
          <Field label="Ville" htmlFor="city">
            <Input value={form.city ?? ''} onChange={(e) => set('city')(e.target.value)} />
          </Field>
          <Field label="Horaires" htmlFor="openingHours" className="sm:col-span-2">
            <Textarea
              value={form.openingHours ?? ''}
              onChange={(e) => set('openingHours')(e.target.value)}
              rows={2}
            />
          </Field>
          <Field label="Presentation" htmlFor="aboutText" className="sm:col-span-2">
            <Textarea
              value={form.aboutText ?? ''}
              onChange={(e) => set('aboutText')(e.target.value)}
              rows={4}
            />
          </Field>
          <Field label="Mentions legales" htmlFor="legalNotice" className="sm:col-span-2">
            <Textarea
              value={form.legalNotice ?? ''}
              onChange={(e) => set('legalNotice')(e.target.value)}
              rows={4}
            />
          </Field>

          <Button
            className="sm:col-span-2 justify-self-start"
            loading={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await api.put('/company', form);
                toast.success('Informations enregistrees.');
              } catch (error) {
                toast.error(messageOf(error));
              } finally {
                setSaving(false);
              }
            }}
          >
            Enregistrer
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Liste d'attente
